import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Activity, Heart, Shield, Calendar, AlertTriangle, Users,
  Loader2, Zap, FileWarning, ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_CONFIG = {
  READY:              { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', bar: 'bg-emerald-500' },
  CAUTION:            { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30',   bar: 'bg-amber-500' },
  REDUCE_LOAD:        { bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/30',  bar: 'bg-orange-500' },
  RECOVERY_EMPHASIS:  { bg: 'bg-sky-500/15',     text: 'text-sky-400',     border: 'border-sky-500/30',     bar: 'bg-sky-500' },
  MONITOR_CLOSELY:    { bg: 'bg-rose-500/15',    text: 'text-rose-400',    border: 'border-rose-500/30',    bar: 'bg-rose-500' },
  RTP_WARNING:        { bg: 'bg-purple-500/15',  text: 'text-purple-400',  border: 'border-purple-500/30',  bar: 'bg-purple-500' },
};

export default function TeamPerformanceDashboardPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('all');
  const [loading, setLoading] = useState(true);

  // Data from existing endpoints
  const [readinessData, setReadinessData] = useState([]);
  const [wellnessData, setWellnessData] = useState(null);
  const [rtpData, setRtpData] = useState(null);
  const [sessionsData, setSessionsData] = useState([]);

  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
  }, [api]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const teamParam = teamId !== 'all' ? teamId : null;

    try {
      const promises = [];

      // 1. Readiness from existing engine
      if (teamParam) {
        promises.push(api.get(`/readiness/team/${teamParam}?date=${today}`));
      } else {
        // For "all teams", fetch each team's readiness and merge
        const teamRes = await api.get('/teams');
        const allTeams = teamRes.data || [];
        const readinessPromises = allTeams.map(t =>
          api.get(`/readiness/team/${t.id}?date=${today}`).catch(() => ({ data: [] }))
        );
        const readinessResults = await Promise.all(readinessPromises);
        const merged = readinessResults.flatMap(r => Array.isArray(r.data) ? r.data : []);
        promises.push(Promise.resolve({ data: merged }));
      }

      // 2. Wellness today (includes missing data)
      const wellnessUrl = teamParam ? `/wellness/today?team_id=${teamParam}` : '/wellness/today';
      promises.push(api.get(wellnessUrl));

      // 3. RTP summary
      const rtpUrl = teamParam ? `/rtp/summary?team_id=${teamParam}` : '/rtp/summary';
      promises.push(api.get(rtpUrl));

      // 4. Today's sessions
      const sessUrl = teamParam ? `/sessions/by-date/${today}?team_id=${teamParam}` : `/sessions/by-date/${today}`;
      promises.push(api.get(sessUrl));

      const [readRes, wellRes, rtpRes, sessRes] = await Promise.all(promises);

      setReadinessData(Array.isArray(readRes.data) ? readRes.data : []);
      setWellnessData(wellRes.data);
      setRtpData(rtpRes.data);
      setSessionsData(Array.isArray(sessRes.data) ? sessRes.data : []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [api, teamId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Derived data — purely from existing endpoint responses, NO new calculations

  // Deduplicate readiness data for athletes in multiple teams
  const deduped = new Map();
  for (const r of readinessData) {
    if (!deduped.has(r.athlete_id)) deduped.set(r.athlete_id, r);
  }
  const uniqueReadiness = [...deduped.values()];

  // Section 1: Readiness distribution (counts from readiness engine output)
  const statusCounts = {};
  for (const r of uniqueReadiness) {
    const s = r.status || 'READY';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  }
  const totalAthletes = uniqueReadiness.length;

  // Section 2: High load athletes (top 5 by rolling_7d_load from readiness inputs)
  const highLoadAthletes = uniqueReadiness
    .filter(r => r.inputs?.rolling_7d_load > 0)
    .sort((a, b) => (b.inputs?.rolling_7d_load || 0) - (a.inputs?.rolling_7d_load || 0))
    .slice(0, 5);

  // Section 3: Wellness alerts (low readiness or high fatigue from existing wellness data)
  const wellnessAlerts = (wellnessData?.entries || [])
    .filter(e => {
      const score = e.readiness_score || e.final_score || 100;
      const fatigue = e.fatigue_score || 0;
      return score < 50 || fatigue >= 4;
    })
    .slice(0, 8);

  // Section 4: RTP athletes (directly from RTP summary)
  const rtpAthletes = rtpData?.athletes_in_rtp || [];

  // Section 5: Today's schedule (directly from planner)
  const todaySessions = sessionsData;

  // Section 6: Missing data (directly from wellness today)
  const missingWellness = wellnessData?.missing || [];
  const missingCount = wellnessData?.stats?.missing_count || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="team-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Activity className="w-6 h-6 text-cyan-400" />
            {t('Team Dashboard', 'Dashboard Squadra')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {t('Aggregated view from existing monitoring systems', 'Vista aggregata dai sistemi di monitoraggio')}
          </p>
        </div>
        <Select value={teamId} onValueChange={setTeamId}>
          <SelectTrigger className="w-full sm:w-[180px] bg-zinc-950 border-zinc-800" data-testid="dashboard-team-filter">
            <SelectValue placeholder="Team" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">{t('All Teams', 'Tutti')}</SelectItem>
            {teams.map(tm => (
              <SelectItem key={tm.id} value={tm.id}>{tm.code || tm.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">

        {/* SECTION 1: Readiness Overview */}
        <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2 xl:col-span-2" data-testid="readiness-overview-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              {t('Team Readiness Overview', 'Panoramica Readiness')}
              <span className="text-xs text-zinc-500 font-normal ml-auto">{totalAthletes} {t('athletes', 'atleti')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totalAthletes === 0 ? (
              <div className="text-sm text-zinc-500 py-4">{t('No readiness data available.', 'Nessun dato readiness disponibile.')}</div>
            ) : (
              <div className="space-y-2">
                {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
                  const count = statusCounts[status] || 0;
                  if (count === 0) return null;
                  const pct = Math.round((count / totalAthletes) * 100);
                  return (
                    <div key={status} className="flex items-center gap-3" data-testid={`status-row-${status}`}>
                      <div className="w-32 shrink-0">
                        <Badge className={`text-[10px] ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                          {status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <div className="flex-1 h-5 bg-zinc-950 rounded-sm overflow-hidden">
                        <div className={`h-full ${cfg.bar} rounded-sm transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className={`text-sm font-bold w-8 text-right ${cfg.text}`}>{count}</span>
                    </div>
                  );
                })}
                {/* Summary badges */}
                <div className="flex items-center gap-2 pt-2 border-t border-zinc-800/50 text-xs text-zinc-500">
                  <span>{statusCounts['READY'] || 0} {t('ready', 'pronti')}</span>
                  <span className="text-zinc-700">|</span>
                  <span>{totalAthletes - (statusCounts['READY'] || 0)} {t('require attention', 'necessitano attenzione')}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 5: Today's Schedule */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="schedule-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-sky-400" />
              {t("Today's Schedule", 'Programma di Oggi')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todaySessions.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">{t('No sessions scheduled today.', 'Nessuna sessione programmata.')}</div>
            ) : (
              <div className="space-y-2">
                {todaySessions.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-2 bg-zinc-950/50 rounded-sm border border-zinc-800/50" data-testid={`session-${s.id}`}>
                    <div>
                      <div className="text-sm font-medium text-zinc-200">{s.title || s.type}</div>
                      <div className="text-[10px] text-zinc-500">
                        {s.time || ''} {s.team_code && `· ${s.team_code}`}
                      </div>
                    </div>
                    <Badge className="text-[10px] bg-zinc-800 text-zinc-400 border-zinc-700">{s.type}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 2: High Load Athletes */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="high-load-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
              <Activity className="w-4 h-4 text-orange-400" />
              {t('High Load Athletes', 'Atleti Carico Elevato')}
              <span className="text-[10px] text-zinc-600 font-normal ml-1">{t('7-day', '7 giorni')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {highLoadAthletes.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">{t('No load data available.', 'Nessun dato carico disponibile.')}</div>
            ) : (
              <div className="space-y-1.5">
                {highLoadAthletes.map((r, i) => (
                  <div key={r.athlete_id} className="flex items-center justify-between p-2 bg-zinc-950/50 rounded-sm" data-testid={`load-athlete-${i}`}>
                    <Link to={`/staff/roster/${r.athlete_id}`} className="text-sm text-zinc-200 hover:text-cyan-400 transition-colors">
                      {r.athlete_name || r.athlete_id.slice(0, 8)}
                    </Link>
                    <span className={`text-sm font-bold ${
                      r.inputs.rolling_7d_load > 3000 ? 'text-rose-400' :
                      r.inputs.rolling_7d_load > 2000 ? 'text-orange-400' : 'text-amber-400'
                    }`}>
                      {Math.round(r.inputs.rolling_7d_load)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 3: Wellness Alerts */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="wellness-alerts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
              <Heart className="w-4 h-4 text-rose-400" />
              {t('Wellness Alerts', 'Alert Wellness')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {wellnessAlerts.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">{t('No wellness concerns today.', 'Nessun problema wellness oggi.')}</div>
            ) : (
              <div className="space-y-1.5">
                {wellnessAlerts.map((e, i) => {
                  const score = e.readiness_score || e.final_score || 0;
                  const issue = e.fatigue_score >= 4 ? t('High fatigue', 'Fatica alta') :
                                e.soreness_score >= 4 ? t('High soreness', 'Dolori alti') :
                                t('Low readiness', 'Readiness bassa');
                  return (
                    <div key={i} className="flex items-center justify-between p-2 bg-zinc-950/50 rounded-sm" data-testid={`wellness-alert-${i}`}>
                      <div>
                        <span className="text-sm text-zinc-200">{e.athlete_name}</span>
                        <span className="text-[10px] text-rose-400 ml-2">{issue}</span>
                      </div>
                      <span className={`text-sm font-bold ${score < 40 ? 'text-rose-400' : 'text-amber-400'}`}>{Math.round(score)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 4: RTP Athletes */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="rtp-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-400" />
              {t('RTP Athletes', 'Atleti RTP')}
              {rtpAthletes.length > 0 && (
                <Badge className="text-[10px] bg-purple-500/15 text-purple-400 border-purple-500/30 ml-auto">{rtpAthletes.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {rtpAthletes.length === 0 ? (
              <div className="text-sm text-zinc-500 py-4">{t('No athletes currently in RTP.', 'Nessun atleta in RTP.')}</div>
            ) : (
              <div className="space-y-1.5">
                {rtpAthletes.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between p-2 bg-zinc-950/50 rounded-sm" data-testid={`rtp-athlete-${i}`}>
                    <div>
                      <span className="text-sm text-zinc-200">{p.athlete_name || p.athlete_id?.slice(0, 8)}</span>
                      <Badge className="text-[9px] ml-2 bg-purple-500/10 text-purple-400 border-purple-500/20">
                        {(p.current_phase || '').replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    {p.caps_count > 0 && (
                      <span className="text-[10px] text-zinc-500">{p.caps_count} {t('caps', 'limiti')}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 6: Missing Data */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="missing-data-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
              <FileWarning className="w-4 h-4 text-amber-400" />
              {t('Missing Data', 'Dati Mancanti')}
              {missingCount > 0 && (
                <Badge className="text-[10px] bg-amber-500/15 text-amber-400 border-amber-500/30 ml-auto">{missingCount}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {missingCount === 0 ? (
              <div className="text-sm text-emerald-400/70 py-4">{t('All athletes have submitted wellness data.', 'Tutti gli atleti hanno compilato il wellness.')}</div>
            ) : (
              <div className="space-y-1.5">
                <div className="text-xs text-zinc-500 mb-2">
                  {missingCount} {t('athletes missing wellness today', 'atleti senza wellness oggi')}
                </div>
                {missingWellness.slice(0, 8).map((a, i) => (
                  <div key={a.id || i} className="flex items-center justify-between p-1.5 text-sm" data-testid={`missing-${i}`}>
                    <span className="text-zinc-400">{a.name}</span>
                    <span className="text-[10px] text-zinc-600">{a.team_code || ''}</span>
                  </div>
                ))}
                {missingCount > 8 && (
                  <div className="text-xs text-zinc-600 pt-1">+{missingCount - 8} {t('more', 'altri')}</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
